/** LLM cost estimate for the help chatbot (pure, testable). */

export interface LlmPricing {
  /** USD per 1M input (prompt) tokens. */
  usdPer1MInput: number;
  /** USD per 1M output (completion) tokens. */
  usdPer1MOutput: number;
}

/** gpt-4o-mini list price at the time of writing. Override via VITE_HELP_LLM_USD_PER_1M_*. */
export const DEFAULT_LLM_PRICING: LlmPricing = {
  usdPer1MInput: 0.15,
  usdPer1MOutput: 0.6,
};

function toNonNegative(value: unknown): number {
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

export function estimateLlmCostUsd(
  promptTokens: number | null | undefined,
  completionTokens: number | null | undefined,
  pricing: LlmPricing = DEFAULT_LLM_PRICING
): number {
  const input = toNonNegative(promptTokens);
  const output = toNonNegative(completionTokens);
  const inRate = toNonNegative(pricing.usdPer1MInput);
  const outRate = toNonNegative(pricing.usdPer1MOutput);
  return (input * inRate + output * outRate) / 1_000_000;
}

type EnvLike = Record<string, string | boolean | undefined>;

function readRate(env: EnvLike, key: string, fallback: number): number {
  const raw = env[key];
  if (raw === undefined || raw === '' || typeof raw === 'boolean') return fallback;
  const n = Number(raw);
  return Number.isFinite(n) && n >= 0 ? n : fallback;
}

export function readLlmPricingFromEnv(env: EnvLike = import.meta.env as unknown as EnvLike): LlmPricing {
  return {
    usdPer1MInput: readRate(env, 'VITE_HELP_LLM_USD_PER_1M_INPUT', DEFAULT_LLM_PRICING.usdPer1MInput),
    usdPer1MOutput: readRate(env, 'VITE_HELP_LLM_USD_PER_1M_OUTPUT', DEFAULT_LLM_PRICING.usdPer1MOutput),
  };
}

/** Small-amount friendly USD formatting: $0.0042, $0.12, $3.40, $120. */
export function formatUsd(value: number): string {
  if (!Number.isFinite(value) || value <= 0) return '$0.00';
  if (value < 0.01) return `$${value.toFixed(4)}`;
  if (value < 100) return `$${value.toFixed(2)}`;
  return `$${Math.round(value).toLocaleString('en-US')}`;
}
